import React from 'react'
import { useCustomReportBuilder } from './hooks/useCustomReportBuilder'
import Step1Setup from './steps/Step1Setup'
import Step2DataSourceFields from './steps/Step2DataSourceFields'
import Step4Filters from './steps/Step4Filters'
import Step5GroupingSorting from './steps/Step5GroupingSorting'
import Step6Calculations from './steps/Step6Calculations'
import Step7Aggregations from './steps/Step7Aggregations'
import Step8Visualization from './steps/Step8Visualization'
import Step9Preview from './steps/Step9Preview'
import Step10SaveSchedule from './steps/Step10SaveSchedule'

const STEPS_META = [
  { step: 1, title: 'Setup', icon: 'settings' },
  { step: 2, title: 'Data & Fields', icon: 'dataset' },
  { step: 3, title: 'Filters', icon: 'filter_list' },
  { step: 4, title: 'Grouping', icon: 'folder_open' },
  { step: 5, title: 'Calculations', icon: 'calculate' },
  { step: 6, title: 'Aggregations', icon: 'functions' },
  { step: 7, title: 'Visualization', icon: 'bar_chart' },
  { step: 8, title: 'Preview', icon: 'preview' },
  { step: 9, title: 'Save / Export', icon: 'save' },
]

export default function CustomReportBuilderModal({
  open,
  reportId = null,
  initialPrefill = null,
  onClose,
  onSaved,
  onTriggerSchedule,
  onTriggerExport,
  companyOptions = [],
  branchOptions = [],
  departmentOptions = [],
  projectOptions = [],
}) {
  const b = useCustomReportBuilder({ open, reportId, initialPrefill, onClose, onSaved })

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm animate-fade-in">
      <div className="flex flex-col w-full max-w-5xl h-[88vh] max-h-[840px] bg-surface rounded-[12px] shadow-2xl overflow-hidden border border-surface-container">
        {/* ── Modal Header & Stepper ── */}
        <div className="shrink-0 bg-surface-container-low border-b border-surface-container px-4 py-2.5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-primary flex items-center justify-center rounded-[8px] text-on-primary">
                <span className="material-symbols-outlined text-lg">auto_graph</span>
              </div>
              <div>
                <h2 className="text-base font-headline font-extrabold text-on-surface leading-tight">
                  {reportId ? 'Edit Custom Report' : 'Custom Report Builder'}
                </h2>
                <p className="text-[11px] text-on-surface-variant">
                  {b.reportName ? b.reportName : 'Configure, join, calculate, and visualize enterprise report data'}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="p-1 text-on-surface-variant hover:text-on-surface hover:bg-surface-container rounded-full transition-colors"
              title="Close Builder"
            >
              <span className="material-symbols-outlined text-lg">close</span>
            </button>
          </div>

          {/* Stepper Navigation Bar: Clean step names in larger font, separated by vertical lines, no pills */}
          <div className="flex items-center justify-between overflow-x-auto py-2 scrollbar-none gap-0.5 md:gap-1">
            {STEPS_META.map((s, idx) => {
              const isActive = b.step === s.step
              const isPast = b.step > s.step
              const isStepDisabled = !b.canGoToStep(s.step)
              return (
                <React.Fragment key={s.step}>
                  <button
                    type="button"
                    disabled={isStepDisabled}
                    onClick={() => b.goToStep(s.step)}
                    className={`shrink-0 text-sm transition-all whitespace-nowrap px-2 py-1 relative ${
                      isActive
                        ? 'font-bold text-primary'
                        : isStepDisabled
                          ? 'font-medium text-on-surface-variant/35 cursor-not-allowed select-none'
                          : isPast
                            ? 'font-medium text-on-surface hover:text-primary cursor-pointer'
                            : 'font-medium text-on-surface-variant/70 hover:text-on-surface cursor-pointer'
                    }`}
                    title={
                      isStepDisabled
                        ? s.step > 1 && !b.reportName.trim()
                          ? 'Enter a report name to proceed'
                          : 'Complete required previous steps to unlock'
                        : undefined
                    }
                  >
                    <span>{s.title}</span>
                    {isActive && (
                      <span className="absolute bottom-0 left-2 right-2 h-[2px] bg-primary rounded-full" />
                    )}
                  </button>

                  {idx < STEPS_META.length - 1 && (
                    <span
                      className={`h-4 w-[1px] shrink-0 self-center select-none transition-colors ${
                        isStepDisabled ? 'bg-outline-variant/30' : 'bg-outline-variant/60'
                      }`}
                      aria-hidden="true"
                    />
                  )}
                </React.Fragment>
              )
            })}
          </div>
        </div>

        {/* ── Modal Body (Active Step Content) ── */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3.5">
          {/* Edit-mode loading state */}
          {b.loadingReport ? (
            <div className="flex flex-col items-center justify-center h-full space-y-3">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-xs font-semibold text-on-surface-variant">Loading saved report...</p>
            </div>
          ) : b.loadReportError ? (
            <div className="flex flex-col items-center justify-center h-full space-y-4 text-center">
              <div className="w-12 h-12 rounded-full bg-error-container flex items-center justify-center">
                <span className="material-symbols-outlined text-2xl text-error">error_outline</span>
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-on-surface">Couldn&apos;t load this report</h4>
                <p className="text-xs text-on-surface-variant max-w-sm">{b.loadReportError}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-[8px] text-xs font-bold bg-surface-container text-on-surface hover:bg-surface-container-high"
              >
                Close
              </button>
            </div>
          ) : b.loadingCatalog ? (
            <div className="flex flex-col items-center justify-center h-full space-y-3">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
              <p className="text-xs font-semibold text-on-surface-variant">Loading report catalog metadata...</p>
            </div>
          ) : (
            <>
              {b.step === 1 && (
                <Step1Setup
                  reportName={b.reportName}
                  setReportName={b.setReportName}
                  description={b.description}
                  setDescription={b.setDescription}
                  category={b.category}
                  setCategory={b.setCategory}
                  companyId={b.companyId}
                  setCompanyId={b.setCompanyId}
                  branchId={b.branchId}
                  setBranchId={b.setBranchId}
                  departmentId={b.departmentId}
                  setDepartmentId={b.setDepartmentId}
                  projectId={b.projectId}
                  setProjectId={b.setProjectId}
                  visibility={b.visibility}
                  setVisibility={b.setVisibility}
                  companyOptions={companyOptions}
                  branchOptions={branchOptions}
                  departmentOptions={departmentOptions}
                  projectOptions={projectOptions}
                />
              )}

              {b.step === 2 && (
                <Step2DataSourceFields
                  primaryModule={b.primaryModule}
                  setPrimaryModule={b.setPrimaryModule}
                  secondaryDatasets={b.secondaryDatasets}
                  setSecondaryDatasets={b.setSecondaryDatasets}
                  dataSources={b.catalog.dataSources}
                  selectedFields={b.selectedFields}
                  setSelectedFields={b.setSelectedFields}
                  allFields={b.catalog.fields}
                />
              )}

              {b.step === 3 && (
                <Step4Filters
                  filterLogic={b.filterLogic}
                  setFilterLogic={b.setFilterLogic}
                  filterConditions={b.filterConditions}
                  setFilterConditions={b.setFilterConditions}
                  allFields={b.catalog.fields}
                  operators={b.catalog.operators}
                  initialPrefill={initialPrefill}
                />
              )}

              {b.step === 4 && (
                <Step5GroupingSorting
                  grouping={b.grouping}
                  setGrouping={b.setGrouping}
                  sorting={b.sorting}
                  setSorting={b.setSorting}
                  selectedFields={b.selectedFields}
                  allFields={b.catalog.fields}
                />
              )}

              {b.step === 5 && (
                <Step6Calculations
                  calculations={b.calculations}
                  setCalculations={b.setCalculations}
                  calculationTemplates={b.catalog.calculationTemplates}
                  allFields={b.catalog.fields}
                />
              )}

              {b.step === 6 && (
                <Step7Aggregations
                  aggregations={b.aggregations}
                  setAggregations={b.setAggregations}
                  selectedFields={b.selectedFields}
                  allFields={b.catalog.fields}
                />
              )}

              {b.step === 7 && (
                <Step8Visualization
                  visualization={b.visualization}
                  setVisualization={b.setVisualization}
                  selectedFields={b.selectedFields}
                  allFields={b.catalog.fields}
                  fetchPreview={b.fetchPreview}
                  previewData={b.previewData}
                  previewLoading={b.previewLoading}
                  previewError={b.previewError}
                />
              )}

              {b.step === 8 && (
                <Step9Preview
                  previewData={b.previewData}
                  previewLoading={b.previewLoading}
                  previewError={b.previewError}
                  fetchPreview={b.fetchPreview}
                  visualization={b.visualization}
                  grouping={b.grouping}
                  filterConditions={b.filterConditions}
                  filterLogic={b.filterLogic}
                  sorting={b.sorting}
                  allFields={b.catalog.fields}
                />
              )}

              {b.step === 9 && (
                <Step10SaveSchedule
                  reportName={b.reportName}
                  handleSave={b.handleSave}
                  isSaving={b.isSaving}
                  saveError={b.saveError}
                  reportId={reportId}
                  onTriggerSchedule={() =>
                    onTriggerSchedule?.({
                      module: b.primaryModule,
                      reportName: b.reportName,
                      config: b.buildConfigPayload(),
                    })
                  }
                  onTriggerExport={(fmt) =>
                    onTriggerExport?.(b.reportName, b.buildConfigPayload(), fmt)
                  }
                />
              )}
            </>
          )}
        </div>

        {/* ── Modal Footer Controls ── */}
        <div className="shrink-0 bg-surface-container-low border-t border-surface-container px-4 py-2 flex items-center justify-between">
          <button
            type="button"
            disabled={b.step === 1}
            onClick={b.prevStep}
            className="flex items-center gap-1 px-3 py-1.5 rounded-[8px] text-xs font-bold text-on-surface-variant hover:bg-surface-container disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <span className="material-symbols-outlined text-sm">chevron_left</span>
            Back
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 rounded-[8px] text-xs font-bold text-on-surface-variant hover:bg-surface-container"
            >
              Cancel
            </button>

            {b.step < 9 ? (
              <button
                type="button"
                disabled={!b.canProceed()}
                onClick={b.nextStep}
                className="flex items-center gap-1 px-4 py-1.5 rounded-[8px] text-xs font-bold bg-primary text-on-primary shadow-sm hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <span>Next</span>
                <span className="material-symbols-outlined text-sm">chevron_right</span>
              </button>
            ) : (
              <button
                type="button"
                disabled={b.isSaving}
                onClick={() => b.handleSave(false)}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-[8px] text-xs font-bold bg-primary text-on-primary shadow-sm hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <span className="material-symbols-outlined text-sm">check</span>
                <span>{b.isSaving ? 'Saving...' : 'Finish & Save'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
